import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, CheckCircle2, Circle, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { safeFormat } from '@/lib/safeDate';

export default function TasksTab({ tasks, onUpdate, userEmail, isProcessing }) {
  const [showForm, setShowForm] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    due_date: ""
  });

  const handleAddTask = () => {
    const task = {
      ...newTask,
      status: "pending",
      created_by: userEmail,
      created_date: new Date().toISOString()
    };
    
    onUpdate([...tasks, task]);
    setNewTask({ title: "", description: "", due_date: "" });
    setShowForm(false);
  };

  const handleToggleTask = (index) => {
    const updatedTasks = tasks.map((task, i) => {
      if (i === index) {
        return {
          ...task,
          status: task.status === "completed" ? "pending" : "completed",
          completed_date: task.status === "completed" ? "" : new Date().toISOString()
        };
      }
      return task;
    });
    onUpdate(updatedTasks);
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        {!showForm ? (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </Button>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-4">
              <Input
                placeholder="Task title..."
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                className="border-slate-300"
              />
              <Textarea
                placeholder="Task description..."
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                className="border-slate-300"
              />
              <Input
                type="date"
                value={newTask.due_date}
                onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                className="border-slate-300"
              />
              <div className="flex gap-3">
                <Button 
                  onClick={handleAddTask}
                  disabled={!newTask.title || isProcessing}
                  className="bg-primary hover:bg-primary/90"
                >
                  Add Task
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setNewTask({ title: "", description: "", due_date: "" });
                  }}
                  className="border-slate-300"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {tasks.length > 0 ? (
        <div className="space-y-3">
          {tasks.map((task, index) => (
            <Card key={index} className="border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => handleToggleTask(index)}
                    className="mt-1 text-slate-400 hover:text-primary transition-colors"
                  >
                    {task.status === "completed" ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Circle className="w-5 h-5" />
                    )}
                  </button>
                  <div className="flex-1">
                    <h4 className={`font-semibold text-slate-900 mb-1 ${task.status === "completed" ? 'line-through text-slate-500' : ''}`}>
                      {task.title}
                    </h4>
                    {task.description && (
                      <p className="text-sm text-slate-600 mb-2">{task.description}</p>
                    )}
                    <div className="flex gap-3 text-xs text-slate-500">
                      {task.due_date && (
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="w-3 h-3" />
                          <span>Due: {safeFormat(new Date(task.due_date), "MMM d, yyyy")}</span>
                        </div>
                      )}
                      <span>Created by {task.created_by}</span>
                    </div>
                  </div>
                  {task.status === "completed" && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0 pointer-events-none">
                      Completed
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-slate-400">
          <CheckCircle2 className="w-16 h-16 mx-auto mb-4" />
          <p>No tasks yet</p>
        </div>
      )}
    </div>
  );
}